import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import type { LangCode } from './i18n';
import { getTranslation } from './i18n';

export type { LangCode };

export const SUPPORTED_LANGUAGES: { code: LangCode; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'ru', name: 'Русский' },
  { code: 'es', name: 'Español' },
  { code: 'de', name: 'Deutsch' },
  { code: 'fr', name: 'Français' },
  { code: 'pt', name: 'Português' },
  { code: 'zh', name: '中文' },
  { code: 'ja', name: '日本語' },
  { code: 'ko', name: '한국어' },
  { code: 'ar', name: 'العربية' },
  { code: 'it', name: 'Italiano' },
  { code: 'pl', name: 'Polski' },
  { code: 'tr', name: 'Türkçe' },
  { code: 'vi', name: 'Tiếng Việt' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'id', name: 'Bahasa Indonesia' },
  { code: 'lt', name: 'Lietuvių' },
];

const LANG_KEY = 'ofm_lang_pref';

const BROWSER_TO_LANG: Record<string, LangCode> = {
  en: 'en', ru: 'ru', es: 'es', de: 'de', fr: 'fr', pt: 'pt', 'pt-BR': 'pt',
  zh: 'zh', 'zh-CN': 'zh', 'zh-TW': 'zh', ja: 'ja', ko: 'ko', ar: 'ar',
  it: 'it', pl: 'pl', tr: 'tr', vi: 'vi', nl: 'nl', id: 'id', lt: 'lt',
};

export function getBrowserLanguage(): LangCode {
  if (typeof window === 'undefined') return 'en';
  const nav = navigator.language || (navigator as any).userLanguage || '';
  const main = nav.split('-')[0].toLowerCase();
  return BROWSER_TO_LANG[nav] || BROWSER_TO_LANG[main] || 'en';
}

export function getStoredLanguage(): LangCode | null {
  if (typeof window === 'undefined') return null;
  const saved = localStorage.getItem(LANG_KEY);
  if (saved && SUPPORTED_LANGUAGES.some(l => l.code === saved)) return saved as LangCode;
  return null;
}

export function setStoredLanguage(lang: LangCode): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(LANG_KEY, lang);
  }
}

type TFunc = (key: string, params?: Record<string, string>) => string;

interface LanguageContextValue {
  lang: LangCode;
  setLang: (lang: LangCode) => void;
  t: TFunc;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<LangCode>(() => {
    const stored = getStoredLanguage();
    if (stored) return stored;
    return getBrowserLanguage();
  });

  useEffect(() => {
    const stored = getStoredLanguage();
    if (!stored) {
      const browser = getBrowserLanguage();
      setLangState(browser);
      setStoredLanguage(browser);
    }
  }, []);

  const setLang = useCallback((newLang: LangCode) => {
    setLangState(newLang);
    setStoredLanguage(newLang);
  }, []);

  const t = useMemo<TFunc>(() => (key: string, params?: Record<string, string>) => 
    getTranslation(key, lang, params), [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
